/**
 * Pure phrasing helpers for the notifications layer (#907) — the factual line
 * each notification renders in the unified attention bell. Kept dependency-free
 * + in one place so the bell and the tests read identically, mirroring
 * `reminderText.ts`. No rendering here.
 *
 * §A9 brand firewall: a notification is the CALLER's OWN record that a note
 * happened on a task they care about — either they were @mentioned, or they
 * subscribed to that task's notes. Every phrase is a calm, factual line about
 * THE WORK + who wrote on it: *"Alex noted on 'Data collection'"* /
 * *"Alex mentioned you in 'Governance'"*. It is NEVER framed as a feed of what
 * other people are doing, NEVER a count / tally / ranking about a person. The
 * automated banlist is substring-based, so the copy avoids any
 * activity-feed / surveillance framing.
 */

import type { TaskNotification } from "../api/useNotifications";

/** A notification phrased for the bell — a glyph + a single factual line + an optional snippet. */
export interface BellNotificationLine {
  /** A small glyph by kind (`@` for a mention, `💬` for a subscribed-note). */
  glyph: string;
  /** The factual line ("Alex mentioned you in 'Governance'"). */
  primary: string;
  /** The note's short snippet, when the API supplied one — else null. */
  snippet: string | null;
}

/** The actor's display name, or a calm fallback when it didn't resolve. */
function actorName(notification: TaskNotification): string {
  return notification.actor_display_name?.trim() || "Someone";
}

/** The node's title, or a calm fallback when it didn't resolve. */
function nodeTitle(notification: TaskNotification): string {
  return notification.node_title?.trim() || "a task";
}

/**
 * Phrase ONE notification for the bell feed (§A9). A mention reads
 * *"<actor> mentioned you in '<task>'"*; a subscribed-note reads
 * *"<actor> noted on '<task>'"* — both about the work, the caller's own
 * record. The note snippet (when present) rides as a secondary line so the
 * member can see what was said without leaving the bell. Never names anyone but
 * the single actor who wrote the note; never aggregates.
 */
export function describeNotificationForBell(notification: TaskNotification): BellNotificationLine {
  const actor = actorName(notification);
  const title = nodeTitle(notification);
  const snippet = notification.note_snippet?.trim() || null;
  if (notification.kind === "mention") {
    return { glyph: "@", primary: `${actor} mentioned you in “${title}”`, snippet };
  }
  // note_subscribed — a note landed on a task the caller chose to follow.
  return { glyph: "💬", primary: `${actor} noted on “${title}”`, snippet };
}

/** True when a notification is unread (the row gets the distinct treatment). */
export function isUnread(notification: TaskNotification): boolean {
  return notification.read_at == null;
}

/**
 * The badge label for the toolbar bell. A small cap keeps it tidy ("9+") so a
 * large unread count never blows out the chrome. Returns null when there's
 * nothing unread (no badge shown).
 */
export function badgeLabel(unreadCount: number): string | null {
  if (unreadCount <= 0) return null;
  return unreadCount > 9 ? "9+" : String(unreadCount);
}
